import { GEO_KINDS, forwardGeoKey, reverseGeoKey, withGeoCache } from "./geo-cache.server";
import { cachedPlacesFetch, PLACES_TTL } from "./places-http-cache.server";
import { createServerFn } from "@tanstack/react-start";
import { canonicalCityName } from "./city-endonym";
import { googleCityFromComponents, isCityLevelResult, pickCityGeocodeResult } from "./city-destination";
import { z } from "zod";
import {
  locationNameFromGoogleComponents,
  locationNameFromNominatimAddress,
  type ResolvedLocationName,
} from "./location-geocoding";
import {
  buildGeoIdentity,
  partsFromGoogleComponents,
  partsFromNominatimAddress,
  type GeoIdentity,
} from "./geo-identity";

const Input = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  language: z.string().min(2).max(8).default("pt"),
});

const cache = new Map<string, { value: ResolvedLocationName; expiresAt: number }>();
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const MAX_CACHE_ENTRIES = 500;

const cacheKey = (lat: number, lng: number, language: string) =>
  `${lat.toFixed(4)},${lng.toFixed(4)}|${language.toLowerCase()}`;

function remember(key: string, value: ResolvedLocationName) {
  if (cache.size >= MAX_CACHE_ENTRIES) {
    const oldest = cache.keys().next().value;
    if (oldest) cache.delete(oldest);
  }
  cache.set(key, { value, expiresAt: Date.now() + CACHE_TTL_MS });
  return value;
}

async function reverseWithNominatim(
  lat: number,
  lng: number,
  language: string,
): Promise<ResolvedLocationName | null> {
  const url = new URL("https://nominatim.openstreetmap.org/reverse");
  url.searchParams.set("format", "jsonv2");
  url.searchParams.set("addressdetails", "1");
  url.searchParams.set("zoom", "18");
  url.searchParams.set("lat", String(lat));
  url.searchParams.set("lon", String(lng));
  url.searchParams.set("accept-language", language);
  const response = await fetch(url, {
    headers: {
      "User-Agent": "Routify/1.0 (support@routify-tour.com)",
      Accept: "application/json",
    },
    signal: AbortSignal.timeout(4500),
  });
  if (!response.ok) return null;
  const payload = (await response.json()) as { address?: Record<string, string> };
  return locationNameFromNominatimAddress(payload.address);
}

async function reverseWithGoogle(
  lat: number,
  lng: number,
  language: string,
): Promise<ResolvedLocationName | null> {
  const apiKey =
    process.env.GOOGLE_MAPS_SERVER_API_KEY || process.env.GOOGLE_MAPS_API_KEY || "";
  if (!apiKey) return null;
  const url = new URL("https://maps.googleapis.com/maps/api/geocode/json");
  url.searchParams.set("latlng", `${lat},${lng}`);
  url.searchParams.set("language", language.toLowerCase().startsWith("pt") ? "pt-PT" : language);
  url.searchParams.set("key", apiKey);
  const response = await cachedPlacesFetch(
    url.toString(),
    { method: "GET", signal: AbortSignal.timeout(4500) },
    { kind: "geocodeReverse", ttlMs: PLACES_TTL.geocodeReverse, origin: "geocode_reverse", sku: "reverse_geocoding" },
  );
  if (!response.ok) return null;
  const payload = (await response.json()) as {
    results?: Array<{
      address_components?: Array<{
        long_name?: string;
        short_name?: string;
        types?: string[];
      }>;
    }>;
  };
  for (const result of payload.results ?? []) {
    const resolved = locationNameFromGoogleComponents(result.address_components);
    if (resolved) return resolved;
  }
  return null;
}

export type ForwardGeocodedLocation = ResolvedLocationName & {
  lat: number;
  lng: number;
  /** Nome de apresentação ao utilizador (exónimo escrito no pedido). */
  displayName?: string;
  /** Fornecedor que resolveu o destino. */
  provider?: "osm" | "google";
  /** ID estável do fornecedor; coordenadas são apenas fallback de identidade. */
  providerPlaceId?: string;
  /**
   * Identidade geográfica completa. A localidade pedida (ex.: Vidago) nunca é
   * substituída pelo município (ex.: Chaves) — a hierarquia fica preservada.
   */
  identity: GeoIdentity;
};

/** Logs de diagnóstico do resolvedor (temporários, desligados por defeito). */
const GEO_DEBUG = process.env.GEO_RESOLVER_DEBUG === "1";
const geoLog = (...args: unknown[]) => {
  if (GEO_DEBUG) console.info("[geo-resolver]", ...args);
};

const ForwardInput = z.object({
  city: z.string().trim().min(2).max(120),
  country: z.string().trim().max(120).optional(),
  language: z.string().min(2).max(8).default("pt"),
});


const forwardCache = new Map<string, { value: ForwardGeocodedLocation; expiresAt: number }>();

function rememberForward(key: string, value: ForwardGeocodedLocation) {
  if (forwardCache.size >= MAX_CACHE_ENTRIES) {
    const oldest = forwardCache.keys().next().value;
    if (oldest) forwardCache.delete(oldest);
  }
  forwardCache.set(key, { value, expiresAt: Date.now() + CACHE_TTL_MS });
  return value;
}

async function forwardWithNominatim(
  city: string,
  country: string | undefined,
  language: string,
): Promise<ForwardGeocodedLocation | null> {
  const url = new URL("https://nominatim.openstreetmap.org/search");
  url.searchParams.set("format", "jsonv2");
  url.searchParams.set("addressdetails", "1");
  url.searchParams.set("namedetails", "1");
  url.searchParams.set("limit", "6");
  url.searchParams.set("q", [canonicalCityName(city), country].filter(Boolean).join(", "));
  url.searchParams.set("accept-language", language);
  const response = await fetch(url, {
    headers: {
      "User-Agent": "Routify/1.0 (support@routify-tour.com)",
      Accept: "application/json",
    },
    signal: AbortSignal.timeout(4500),
  });
  if (!response.ok) {
    geoLog("nominatim http", response.status, "for", city);
    return null;
  }
  const payload = (await response.json()) as Array<{
    lat?: string;
    lon?: string;
    class?: string;
    type?: string;
    addresstype?: string;
    display_name?: string;
    address?: Record<string, string>;
    namedetails?: Record<string, string>;
  }>;
  if (GEO_DEBUG) {
    for (const item of payload) {
      geoLog("nominatim candidate", {
        class: item.class,
        type: item.type,
        addresstype: item.addresstype,
        display_name: item.display_name,
        lat: item.lat,
        lon: item.lon,
        accepted: isCityLevelResult(item, city),
      });
    }
  }
  // Só aceitamos resultados ao nível cidade/localidade: um estado, região ou
  // país nunca é destino válido para uma rota urbana.
  const picked = pickCityGeocodeResult(payload, city);
  if (!picked) {
    geoLog("nominatim rejected all candidates for", city, "(no valid locality)");
    return null;
  }
  const first = picked.item as (typeof payload)[number];
  const { lat, lng } = picked;
  const resolved = locationNameFromNominatimAddress(first?.address);
  if (!resolved) return null;
  const searchable = picked.city;
  const identity = buildGeoIdentity({
    requestedName: searchable,
    parts: { ...partsFromNominatimAddress(first?.address), locality: searchable },
    lat,
    lng,
  });
  const out: ForwardGeocodedLocation = {
    ...resolved,
    country: picked.country || resolved.country,
    countryCode: picked.countryCode || resolved.countryCode,
    city: identity.displayName || searchable,
    displayName: picked.displayName,
    provider: "osm",
    providerPlaceId: picked.providerPlaceId,
    lat,
    lng,
    identity,
  };
  geoLog("nominatim resolved", {
    searchName: out.city,
    displayName: out.displayName,
    countryCode: out.countryCode,
    lat,
    lng,
    providerPlaceId: out.providerPlaceId,
  });
  return out;
}



async function forwardWithGoogle(
  city: string,
  country: string | undefined,
  language: string,
): Promise<ForwardGeocodedLocation | null> {
  const apiKey =
    process.env.GOOGLE_MAPS_SERVER_API_KEY || process.env.GOOGLE_MAPS_API_KEY || "";
  if (!apiKey) return null;
  const url = new URL("https://maps.googleapis.com/maps/api/geocode/json");
  url.searchParams.set("address", [canonicalCityName(city), country].filter(Boolean).join(", "));
  url.searchParams.set("language", language.toLowerCase().startsWith("pt") ? "pt-PT" : language);
  url.searchParams.set("key", apiKey);
  const response = await cachedPlacesFetch(
    url.toString(),
    { method: "GET", signal: AbortSignal.timeout(4500) },
    { kind: "geocodeForward", ttlMs: PLACES_TTL.geocodeForward, origin: "geocode_forward", sku: "geocoding" },
  );
  if (!response.ok) {
    geoLog("google http", response.status, "for", city);
    return null;
  }
  const payload = (await response.json()) as {
    results?: Array<{
      place_id?: string;
      geometry?: { location?: { lat?: number; lng?: number } };
      address_components?: Array<{
        long_name?: string;
        short_name?: string;
        types?: string[];
      }>;
    }>;
  };
  for (const result of payload.results ?? []) {
    // Nunca aceitamos país/estado/região: só locality/postal_town.
    const resolved = googleCityFromComponents(result.address_components, city);
    const lat = result.geometry?.location?.lat;
    const lng = result.geometry?.location?.lng;
    if (resolved && Number.isFinite(lat) && Number.isFinite(lng)) {
      const identity = buildGeoIdentity({
        requestedName: resolved.city,
        parts: { ...partsFromGoogleComponents(result.address_components), locality: resolved.city },
        lat: lat as number,
        lng: lng as number,
      });
      const out: ForwardGeocodedLocation = {
        ...resolved,
        city: identity.displayName || resolved.city,
        displayName: resolved.displayName,
        provider: "google",
        providerPlaceId: result.place_id,
        lat: lat as number,
        lng: lng as number,
        identity,
      };
      geoLog("google resolved", {
        searchName: out.city,
        displayName: out.displayName,
        countryCode: out.countryCode,
        lat: out.lat,
        lng: out.lng,
        providerPlaceId: out.providerPlaceId,
      });
      return out;
    }
  }
  geoLog("google rejected all candidates for", city, "(no locality/postal_town)");
  return null;
}


export const reverseGeocodeLocation = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => Input.parse(data))
  .handler(async ({ data }): Promise<ResolvedLocationName | null> => {
    const key = cacheKey(data.lat, data.lng, data.language);
    const cached = cache.get(key);
    if (cached && cached.expiresAt > Date.now()) return cached.value;
    if (cached) cache.delete(key);

    // Camada persistente (partilhada entre instâncias) + single-flight por chave.
    const resolved = await withGeoCache<ResolvedLocationName>(
      GEO_KINDS.reverse,
      reverseGeoKey(data.lat, data.lng, data.language),
      async () => {
        // Fornecedor gratuito primeiro, sem passar pelo limitador do Google.
        try {
          const nominatim = await reverseWithNominatim(data.lat, data.lng, data.language);
          if (nominatim) return nominatim;
        } catch (error) {
          console.warn("Nominatim reverse geocoding failed", error);
        }

        const { checkGoogleApiRateLimit } = await import("./quota.server");
        if (!(await checkGoogleApiRateLimit("geocoding")).ok) return null;

        try {
          const google = await reverseWithGoogle(data.lat, data.lng, data.language);
          if (google) return google;
        } catch (error) {
          console.warn("Google reverse geocoding failed", error);
        }

        return null;
      },

      { language: data.language },
    );
    if (resolved) return remember(key, resolved);
    return null;
  });

export const forwardGeocodeLocation = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => ForwardInput.parse(data))
  .handler(async ({ data }): Promise<ForwardGeocodedLocation | null> => {
    const key = `${data.city.toLowerCase()}|${(data.country || "").toLowerCase()}|${data.language.toLowerCase()}`;
    const cached = forwardCache.get(key);
    if (cached && cached.expiresAt > Date.now()) return cached.value;
    if (cached) forwardCache.delete(key);

    const resolved = await withGeoCache<ForwardGeocodedLocation>(
      GEO_KINDS.forward,
      forwardGeoKey(data.city, data.country, data.language),
      async () => {
        // O Nominatim é gratuito: corre SEMPRE primeiro, fora de qualquer
        // limitador. O limitador só pode travar a chamada paga ao Google.
        try {
          const nominatim = await forwardWithNominatim(data.city, data.country, data.language);
          if (nominatim) return nominatim;
        } catch (error) {
          console.warn("Nominatim forward geocoding failed", error);
        }

        const { checkGoogleApiRateLimit } = await import("./quota.server");
        if (!(await checkGoogleApiRateLimit("geocoding")).ok) {
          geoLog("google skipped (rate limited) for", data.city);
          return null;
        }

        try {
          const google = await forwardWithGoogle(data.city, data.country, data.language);
          if (google) return google;
        } catch (error) {
          console.warn("Google forward geocoding failed", error);
        }

        geoLog("no destination for", data.city, "(nominatim + google exhausted)");
        return null;
      },

      { language: data.language },
    );
    if (resolved) return rememberForward(key, resolved);
    return null;
  });


/**
 * Candidatos homónimos para um nome de destino (ex.: "Caracal" existe na
 * Roménia e no México). Usa apenas Nominatim: gratuito, sem créditos, sem IA.
 */
const CandidatesInput = z.object({
  name: z.string().trim().min(2).max(120),
  language: z.string().min(2).max(8).default("pt"),
});

export type DestinationCandidateRaw = {
  locality: string;
  region?: string;
  country: string;
  countryCode: string;
  lat: number;
  lng: number;
  importance: number;
  kind: string;
  placeId?: string;
};

const candidatesCache = new Map<
  string,
  { value: DestinationCandidateRaw[]; expiresAt: number }
>();

export const findDestinationCandidates = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => CandidatesInput.parse(data))
  .handler(async ({ data }): Promise<DestinationCandidateRaw[]> => {
    const key = `${data.name.toLowerCase()}|${data.language.toLowerCase()}`;
    const cached = candidatesCache.get(key);
    if (cached && cached.expiresAt > Date.now()) return cached.value;
    if (cached) candidatesCache.delete(key);

    const persisted = await withGeoCache<DestinationCandidateRaw[]>(
      GEO_KINDS.candidates,
      key,
      async () => resolveDestinationCandidates(data.name, data.language),
      { isEmpty: (value) => value.length === 0, language: data.language },
    );
    const out = persisted ?? [];
    if (candidatesCache.size >= MAX_CACHE_ENTRIES) {
      const oldest = candidatesCache.keys().next().value;
      if (oldest) candidatesCache.delete(oldest);
    }
    candidatesCache.set(key, { value: out, expiresAt: Date.now() + CACHE_TTL_MS });
    return out;
  });

async function resolveDestinationCandidates(
  name: string,
  language: string,
): Promise<DestinationCandidateRaw[]> {
  {
    // Só Nominatim (gratuito): não passa pelo limitador de chamadas Google.


    try {
      const url = new URL("https://nominatim.openstreetmap.org/search");
      url.searchParams.set("format", "jsonv2");
      url.searchParams.set("addressdetails", "1");
      url.searchParams.set("limit", "10");
      url.searchParams.set("q", name);
      url.searchParams.set("accept-language", language);
      const response = await fetch(url, {
        headers: {
          "User-Agent": "Routify/1.0 (support@routify-tour.com)",
          Accept: "application/json",
        },
        signal: AbortSignal.timeout(4500),
      });
      if (!response.ok) return [];
      const payload = (await response.json()) as Array<{
        place_id?: number;
        lat?: string;
        lon?: string;
        name?: string;
        type?: string;
        addresstype?: string;
        importance?: number;
        address?: Record<string, string>;
      }>;
      const out: DestinationCandidateRaw[] = [];
      for (const item of payload) {
        const lat = Number.parseFloat(item.lat ?? "");
        const lng = Number.parseFloat(item.lon ?? "");
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
        const address = item.address ?? {};
        const locality =
          address.city ||
          address.town ||
          address.village ||
          address.municipality ||
          item.name ||
          "";
        const country = address.country || "";
        const countryCode = (address.country_code || "").toUpperCase();
        if (!locality || !countryCode) continue;
        out.push({
          locality,
          region: address.state || address.county || undefined,
          country,
          countryCode,
          lat,
          lng,
          importance: Number(item.importance) || 0,
          kind: item.addresstype || item.type || "",
          placeId: Number.isFinite(item.place_id) ? `osm:${item.place_id}` : undefined,
        });
      }
      return out;
    } catch (error) {
      console.warn("Destination candidates lookup failed", error);
      return [];
    }
  }
}
